<template>
  <template v-if="transaction">
    <RouterLink
      v-if="transactionParentRoute"
      :to="transactionParentRoute"
      class="mb-4 flex items-center justify-center rounded-lg bg-background p-4 text-sm text-primary"
    >
      View parent
      {{ transaction.orderId ? "sale" : "donation" }}
      <IconGoTo class="ml-2" />
    </RouterLink>

    <BaseWarningBox
      v-if="isMismatchedCurrency"
      class="mb-4"
    >
      <p>
        This transaction was made in a currency other than
        <strong>{{ merchantCurrency }}</strong
        >. It is recorded in its original currency but will
        <strong>not</strong> appear in any app or portal
        reporting.
      </p>
    </BaseWarningBox>

    <section class="rounded-lg bg-background p-4">
      <div class="mb-4 flex flex-col items-center">
        <div
          class="flex items-center justify-center text-lg font-semibold"
        >
          <template
            v-if="transaction.status === 'successful'"
          >
            <span> {{ typeLabel }} Successful </span>
            <IconCheckCircle class="ml-2 text-positive" />
          </template>
          <template
            v-else-if="transaction.status === 'failed'"
          >
            <span> {{ typeLabel }} Failed </span>
            <IconXCircle class="ml-2 text-error" />
          </template>
          <template v-else>
            <span>{{ typeLabel }} {{ statusLabel }}</span>
            <IconExclamationCircle
              class="ml-2 text-warning"
            />
          </template>
        </div>

        <div
          :class="[
            transaction.status === 'successful'
              ? {
                  payment: 'text-background-fg',
                  refund: 'text-error',
                }[transaction.type]
              : 'text-black/30',
            'mb-2 flex flex-col items-center text-right',
          ]"
        >
          <span class="line-clamp-1 text-3xl font-bold">
            <template v-if="transaction.type === 'refund'"
              >-</template
            >
            {{
              formatToCurrencyString(
                transaction.amountTransactedTotal,
                transaction.currency
              )
            }}
          </span>
          <span
            v-if="
              transaction.transactionMethod ===
                'pos_card' &&
              transaction.isDcc &&
              transaction.isDccFieldsPopulated
            "
            class="text-sm font-semibold text-background-fg/70"
          >
            <template v-if="transaction.type === 'refund'"
              >-</template
            >
            {{
              formatToCurrencyString(
                transaction.dccCardCurrencyAmount,
                transaction.dccCardholderCurrency
              )
            }}
          </span>
        </div>

        <PaymentDetailChip
          :payment="transaction"
          large
          class="self-center"
        />
      </div>

      <dl>
        <template
          v-if="
            transaction.amountTransactedGratuity ||
            transaction.amountTransactedCashback
          "
        >
          <div
            v-if="transaction.amountTransactedGratuity"
            class="flex items-center justify-between"
          >
            <dt
              class="text-sm leading-loose text-background-fg/70"
            >
              Gratuity
              <template
                v-if="transaction.type === 'refund'"
              >
                (refunded)
              </template>
            </dt>
            <dd class="text-base font-semibold">
              {{
                formatToCurrencyString(
                  transaction.amountTransactedGratuity,
                  transaction.currency
                )
              }}
            </dd>
          </div>
          <div
            v-if="transaction.amountTransactedCashback"
            class="flex items-center justify-between"
          >
            <dt
              class="text-sm leading-loose text-background-fg/70"
            >
              Cashback
              <template
                v-if="transaction.type === 'refund'"
              >
                (refunded)
              </template>
            </dt>
            <dd class="text-base font-semibold">
              {{
                formatToCurrencyString(
                  transaction.amountTransactedCashback,
                  transaction.currency
                )
              }}
            </dd>
          </div>

          <div class="flex items-center justify-between">
            <dt
              class="text-sm leading-loose text-background-fg/70"
            >
              Subtotal
              <template
                v-if="transaction.type === 'refund'"
              >
                (refunded)
              </template>
            </dt>
            <dd class="text-base font-semibold">
              {{
                formatToCurrencyString(
                  transaction.amountTransactedGross,
                  transaction.currency
                )
              }}
            </dd>
          </div>

          <div
            class="my-2 h-px w-full bg-background-fg/8"
          />
        </template>

        <template
          v-if="
            transaction.transactionMethod === 'pos_card'
          "
        >
          <div
            v-if="transaction.acquirerResponseCode"
            class="flex items-center justify-between"
          >
            <dt
              class="text-sm leading-loose text-background-fg/70"
            >
              Response Code
            </dt>
            <dd class="text-base font-semibold">
              {{ transaction.acquirerResponseCode }}
            </dd>
          </div>

          <div
            v-if="transaction.acquirerAuthCode"
            class="flex items-center justify-between"
          >
            <dt
              class="text-sm leading-loose text-background-fg/70"
            >
              Auth Code
            </dt>
            <dd class="text-base font-semibold">
              {{ transaction.acquirerAuthCode }}
            </dd>
          </div>

          <div
            v-if="transaction.acquirerTerminalId"
            class="flex items-center justify-between"
          >
            <dt
              class="text-sm leading-loose text-background-fg/70"
            >
              TID
            </dt>
            <dd class="text-base font-semibold">
              {{ transaction.acquirerTerminalId }}
            </dd>
          </div>

          <div
            class="my-2 h-px w-full bg-background-fg/8"
          />
        </template>
      </dl>

      <dl>
        <div class="flex items-center justify-between">
          <dt
            class="text-sm leading-loose text-background-fg/70"
          >
            Date
          </dt>
          <dd class="text-base font-semibold">
            {{ formatToDate(transaction.createdAt) }}
          </dd>
        </div>
        <div class="flex items-center justify-between">
          <dt
            class="text-sm leading-loose text-background-fg/70"
          >
            Time
          </dt>
          <dd class="text-base font-semibold">
            {{ formatToTime(transaction.createdAt) }}
          </dd>
        </div>
        <div
          v-if="transaction.terminalSerialNumber"
          class="flex items-center justify-between"
        >
          <dt
            class="text-sm leading-loose text-background-fg/70"
          >
            Terminal Serial Number
          </dt>
          <dd class="text-base font-semibold">
            {{ transaction.terminalSerialNumber }}
          </dd>
        </div>
      </dl>

      <TransactionDetailReceipt
        :transaction-id="transactionId"
      />
    </section>
  </template>

  <BaseInnerLoading v-else-if="isFetching" />

  <ErrorText v-else-if="isError">
    Failed to load transaction
  </ErrorText>
</template>

<script setup lang="ts">
import { pascalize } from "humps"
import type { RouteLocationRaw } from "vue-router"

const { formatToCurrencyString } = useCurrency()

const props = defineProps<{
  transactionId: string
}>()

const {
  data: transaction,
  isError,
  isFetching,
} = useTransaction(props.transactionId)

const { currency: merchantCurrency } = useTenant()

const typeLabel = computed(() =>
  transaction.value
    ? {
        payment: "Payment",
        refund: "Refund",
      }[transaction.value?.type]
    : undefined
)

const statusLabel = computed(() =>
  transaction.value
    ? pascalize(transaction.value?.status)
    : ""
)

const isMismatchedCurrency = computed(
  () =>
    transaction.value?.currency !== merchantCurrency.value
)

const transactionParentRoute = computed<
  RouteLocationRaw | undefined
>(() =>
  transaction.value?.orderId
    ? {
        name: "orderDetail",
        params: {
          orderId: transaction.value.orderId,
        },
      }
    : transaction.value?.donationId
      ? {
          name: "donationDetail",
          params: {
            donationId: transaction.value.donationId,
          },
        }
      : undefined
)
</script>
